import {
    ComponentMappingStatus,
    ComponentMappingStatusSummary,
    ComponentReportMappingState,
    MappingRunMode,
    MappingRunStatus,
} from '../../../../../@types/component-mapping-state';
import {formatDayTime} from '../../../../../utils/date-format';

/** How each derived mapping status is coloured wherever this feature shows one. */
export const MAPPING_STATUS_COLOR: Record<ComponentMappingStatus, 'default' | 'info' | 'success' | 'warning'> = {
    [ComponentMappingStatus.MAPPING_IN_PROGRESS]: 'info',
    [ComponentMappingStatus.NOT_MAPPED]: 'default',
    [ComponentMappingStatus.PARTIALLY_MAPPED]: 'warning',
    [ComponentMappingStatus.CHANGED_SINCE_MAPPING]: 'warning',
    [ComponentMappingStatus.MAPPED]: 'success',
};

/** How each run status is coloured wherever this feature shows one. */
export const RUN_STATUS_COLOR: Record<MappingRunStatus, 'default' | 'info' | 'success' | 'error' | 'warning'> = {
    [MappingRunStatus.QUEUED]: 'info',
    [MappingRunStatus.RUNNING]: 'info',
    [MappingRunStatus.COMPLETED]: 'success',
    [MappingRunStatus.ERROR]: 'error',
    [MappingRunStatus.CANCELLED]: 'warning',
};

/**
 * Builds the Mapping tab's source list: every report source, paired with what this component
 * version has run against it. Kept free of component imports so it can be tested as the plain
 * merge and formatting it is.
 */

/** A report source as the source list reports it. */
export interface ReportSourceOption {
    sourceName: string;
    reportCount: number;
}

/** One row of the source list: a source, and this version's mapping state against it if any. */
export interface MappingSourceRow {
    /** The source name to display and to address the run endpoints with. */
    sourceName: string;
    /** Reports currently in the source, or null when the source itself is no longer listed. */
    reportCount: number | null;
    /** The mapping state, or null when this version has never been run against the source. */
    state: ComponentReportMappingState | null;
    /**
     * Where the version stands against the source now, derived from its records, or null when it
     * has never been run against it. This is what a row leads with: a completed run is a fact about
     * the run, and says nothing about how much of the source it covered.
     */
    status: ComponentMappingStatusSummary | null;
}

/**
 * Pairs each report source with this version's state against it. Sources the version has never
 * been run against are included, because the list is how a source is chosen as well as how past
 * runs are read.
 *
 * <p>A state whose source has since disappeared from the source list still gets a row, with no
 * report count. Dropping it would quietly hide evidence that exists: the records are still there
 * and the workbook still opens on them.</p>
 */
export function buildSourceRows(
    sources: ReportSourceOption[],
    states: ComponentReportMappingState[],
    statuses: ComponentMappingStatusSummary[] = [],
): MappingSourceRow[] {
    const byName = new Map<string, ComponentReportMappingState>();
    states.forEach(state => byName.set(state.sourceName.toLowerCase(), state));
    const statusByName = new Map<string, ComponentMappingStatusSummary>();
    statuses.forEach(status => statusByName.set(status.sourceName.toLowerCase(), status));

    const rows: MappingSourceRow[] = sources.map(source => {
        const key = source.sourceName.toLowerCase();
        const state = byName.get(key) ?? null;
        byName.delete(key);
        return {sourceName: source.sourceName, reportCount: source.reportCount, state,
            status: statusByName.get(key) ?? null};
    });

    byName.forEach(state => rows.push({sourceName: state.sourceName, reportCount: null, state,
        status: statusByName.get(state.sourceName.toLowerCase()) ?? null}));
    return rows;
}

/**
 * What kind of run the last one was, so a date beside a partially mapped source is explained: a
 * test run of a capped sample is not an attempt at the source. Empty when there has been no run.
 */
export function describeRunKind(state: ComponentReportMappingState | null): string {
    switch (state?.runMode) {
        case MappingRunMode.TEST: {
            const limit = toCount(state?.runReportLimit);
            return limit == null ? 'Test run' : `Test run of ${limit.toLocaleString()} report${limit === 1 ? '' : 's'}`;
        }
        case MappingRunMode.UPDATE:
            return 'Update';
        case MappingRunMode.FULL_REMAP:
            return 'Full re-map';
        default:
            return '';
    }
}

/** A run whose state is worth a chip beside the date: anything but the steady completed case. */
export function runNeedsAttention(state: ComponentReportMappingState | null): boolean {
    return state?.runStatus != null && state.runStatus !== MappingRunStatus.COMPLETED;
}

/**
 * One count out of a run's statistics, or null when the snapshot does not carry it as a number.
 *
 * The statistics are stored as a schema-less JSONB document, so a row written by an earlier shape
 * of the snapshot can be missing a field the current one always writes. Reading such a field as a
 * number and formatting it is how this crashed the Mapping tab, so every read goes through here.
 */
export function toCount(value: unknown): number | null {
    if (typeof value === 'string') {
        // Number('') and Number('  ') are both 0, which would report no reports where the count is
        // in fact unknown.
        return value.trim() === '' ? null : toCount(Number(value));
    }
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** When the last successful run against a source finished, or that there has not been one. */
export function describeLastRun(state: ComponentReportMappingState | null): string {
    return formatDayTime(state?.lastRunAt) ?? 'Never run';
}

/**
 * What the most recent run against a source did, in a sentence. Says the mode as well as the
 * counts, because a test run evaluated a capped sample and reading its counts as the source's
 * standing would overstate what has been mapped.
 */
export function describeRunOutcome(state: ComponentReportMappingState | null): string {
    if (!state?.runStatus) {
        return '';
    }
    if (state.runStatus === MappingRunStatus.ERROR) {
        return state.runError ? `Failed: ${state.runError}` : 'Failed';
    }
    const stats = state.runStatistics;
    const processed = toCount(stats?.processed);
    const mapped = toCount(stats?.mapped);
    if (processed == null && mapped == null) {
        return state.runStatus === MappingRunStatus.CANCELLED ? 'Cancelled before any report' : '';
    }

    const evaluated = (processed ?? 0).toLocaleString();
    const total = toCount(stats?.totalReports);
    let scope: string;
    if (total == null) {
        // The snapshot does not say how many reports were in scope, so the count stands alone
        // rather than being given a denominator it does not have.
        scope = `${evaluated} evaluated`;
    } else if (state.runMode === MappingRunMode.TEST) {
        scope = `${evaluated} of test sample of ${total.toLocaleString()} evaluated`;
    } else {
        scope = `${evaluated} of ${total.toLocaleString()} report${total === 1 ? '' : 's'} evaluated`;
    }
    return mapped == null ? scope : `${scope}, ${mapped.toLocaleString()} mapped`;
}

/**
 * Live progress while a run is going, or empty when the run has not reported anything yet and the
 * caller should say it is starting instead.
 */
export function describeRunProgress(state: ComponentReportMappingState | null): string {
    const stats = state?.runStatistics;
    const processed = toCount(stats?.processed);
    const mapped = toCount(stats?.mapped);
    if (processed == null && mapped == null) {
        return '';
    }
    const total = toCount(stats?.totalReports);
    const evaluated = total == null
        ? `${(processed ?? 0).toLocaleString()} reports`
        : `${(processed ?? 0).toLocaleString()} / ${total.toLocaleString()} reports`;
    return `${evaluated} · ${(mapped ?? 0).toLocaleString()} mapped`;
}

/**
 * What a finished run did, as a sentence. Reports that need review and reports that failed are
 * named only when there are any, so a clean run reads as a clean run.
 */
export function describeRunCompletion(state: ComponentReportMappingState | null): string {
    const stats = state?.runStatistics;
    const processed = toCount(stats?.processed);
    const mapped = toCount(stats?.mapped);
    if (processed == null && mapped == null) {
        return '';
    }

    const evaluated = processed ?? 0;
    const parts = [`Evaluated ${evaluated.toLocaleString()} report${evaluated === 1 ? '' : 's'}`];
    if (mapped != null) {
        parts.push(`${mapped.toLocaleString()} mapped`);
    }
    const needsReview = toCount(stats?.needsReview);
    if (needsReview != null && needsReview > 0) {
        parts.push(`${needsReview.toLocaleString()} need review`);
    }
    const failed = toCount(stats?.failed);
    if (failed != null && failed > 0) {
        parts.push(`${failed.toLocaleString()} failed`);
    }
    return `${parts.join(', ')}.`;
}
